import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { getAppConfig } from "../config/config";
import { BVHIAuthenticationBroadcastMessage } from "../context/auth-context-provider";
import { useAuthContext } from "../context/use-auth-context";
import { useBroadcastChannel } from "./use-broadcast-channel";

export const useAuthentication = () => {
  const navigate = useNavigate();
  const { refetch } = useAuthContext();

  const handleBroadcastMessageReveive = useCallback(
    (message: MessageEvent<BVHIAuthenticationBroadcastMessage>) => {
      setTimeout(() => {
        void refetch().then((response) => {
          if (!message.data.isAuthenticated && response.isError) {
            void navigate({ to: "/" });
          }
        });
      }, getAppConfig().postMessageTimeout);
    },
    [navigate, refetch]
  );

  const { postMessage } = useBroadcastChannel<BVHIAuthenticationBroadcastMessage>("bvhi.authentication", handleBroadcastMessageReveive);

  const getCurrentUserStatusCode = useCallback(async (): Promise<number> => {
    return fetch(`${getAppConfig().env.gatewayUrl}/platform/v1/profile/current`)
      .then((raw) => {
        if (!raw.ok) throw new Error(raw.statusText);
        return raw.status;
      })
      .catch(() => {
        return 500;
      });
  }, []);

  const logout = useCallback((): void => {
    postMessage({ isAuthenticated: false });
    try {
      // to logout we need to make a full frame post to
      // the api gateway
      const authorizationForm = document.createElement("form");
      authorizationForm.addEventListener("submit", (e) => {
        e.preventDefault();
      });
      authorizationForm.setAttribute("id", "authorization-form");
      authorizationForm.setAttribute("method", "POST");
      authorizationForm.setAttribute("action", `${getAppConfig().env.gatewayUrl}/logout`);

      const name = "XSRF-TOKEN=";
      const decodedCookie = decodeURIComponent(document.cookie);

      const ca = decodedCookie.split(";").map((ca) => ca.trim());
      const match = ca.find((ca) => ca.indexOf(name) !== -1);
      if (match) {
        const csrfToken = match.substring(name.length, match.length);
        const hiddenInput = document.createElement("input");
        hiddenInput.setAttribute("type", "hidden");
        hiddenInput.setAttribute("name", "_csrf");
        hiddenInput.setAttribute("value", csrfToken);
        authorizationForm.appendChild(hiddenInput);
      }

      document.body.appendChild(authorizationForm);
      authorizationForm.submit();

      // TODO: Handle if logout had problems
    } catch (e) {
      console.error(e);
    }
  }, [postMessage]);

  const login = useCallback((): void => {
    const loginURL = `${getAppConfig().env.gatewayUrl}/oauth2/authorization/keycloak`;
    window.location.href = loginURL;
  }, []);

  const isAuthenticated = useCallback(async (): Promise<boolean> => {
    const status = await getCurrentUserStatusCode();
    if (status === 401) return false;
    if (status === 403) return false;
    if (status === 500) return false;
    postMessage({ isAuthenticated: true });
    return true;
  }, [getCurrentUserStatusCode, postMessage]);

  return { isAuthenticated, login, logout };
};

export type useAuthenticationType = ReturnType<typeof useAuthentication>;
